import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';
import { useCasesData } from '../../hooks/useCasesData';
import { useCaseAccess } from '../../hooks/useCaseAccess';
import { useDashboardDataContext } from '../../contexts/DashboardDataContext';
import {
  buildNextAppointmentByCaseId,
  buildSessionAppointmentsByCaseId,
  collectDashboardActionCaseBuckets,
  getCalendarDatePath,
  isFrequencyOverdueActionable,
  shouldAskForTablou,
} from '../Dashboard/dashboardUtils';
import { formatMeetingFrequencyOverdueBy } from '../../utils/meetingFrequency';
import { Appointment, Case } from '../../types';
import MeetingNotes from './MeetingNotes';
import MeetingNoteFormDialog from '../MeetingNotes/MeetingNoteFormDialog';
import SessionReport from './SessionReport';
import CaseProfileDialog from './CaseProfileDialog';
import CaseTimelineDialog from './CaseTimelineDialog';
import PrepareSessionDialog from '../Dashboard/PrepareSessionDialog';
import CasesPageHeader from './CasesPageHeader';
import CasesToolbar from './CasesToolbar';
import CasesGrid from './CasesGrid';
import ScheduleAppointmentDialog from '../Dashboard/ScheduleAppointmentDialog';
import { useTimedPulse } from '../../hooks/useTimedPulse';
import { useSessionQuestions } from '../../contexts/SessionQuestionsContext';

const Cases: React.FC = () => {
  const navigate = useNavigate();
  const data = useCasesData();
  const access = useCaseAccess();
  const { offerAfterReport } = useSessionQuestions();
  const { upcomingAppointments, appointments, sessionReportCounts, refetch } =
    useDashboardDataContext();
  const [timelineCase, setTimelineCase] = useState<Case | null>(null);
  const [deepenOpen, setDeepenOpen] = useState(false);
  const [deepenCase, setDeepenCase] = useState<Case | null>(null);
  const [profileCase, setProfileCase] = useState<Case | null>(null);
  const [scheduleCaseId, setScheduleCaseId] = useState<string | null>(null);
  const [scheduleSessionNumber, setScheduleSessionNumber] = useState<number | null>(
    null
  );

  const nextAppointmentByCaseId = useMemo(
    () => buildNextAppointmentByCaseId(upcomingAppointments),
    [upcomingAppointments]
  );
  const appointmentBySessionByCaseId = useMemo(
    () => buildSessionAppointmentsByCaseId(upcomingAppointments),
    [upcomingAppointments]
  );
  // Same alert semantics as the dashboard action cards, computed from the data
  // already cached in the dashboard context (no extra Firestore reads).
  const actionBuckets = useMemo(
    () =>
      collectDashboardActionCaseBuckets(
        data.cases.filter((caseItem) => !access.isShadowed(caseItem.id)),
        appointments,
        sessionReportCounts
      ),
    [data.cases, appointments, sessionReportCounts, access]
  );
  const reportsNeededCaseIds = useMemo(
    () => new Set(actionBuckets.reportsNeededCaseIds),
    [actionBuckets]
  );
  /**
   * The ask, not the tablou itself — it goes quiet once the next session is close enough to
   * prepare for, and it belongs to the counselor whose report opened it, so a shadowed case
   * never asks its apprentice. Reaching an old tablou stays possible from its note either way.
   */
  const askTabloulCaseIds = useMemo(() => {
    const preparing = new Set(actionBuckets.prepareSessionCaseIds);
    return new Set(
      data.cases
        .filter(
          (caseItem) =>
            !access.isShadowed(caseItem.id) &&
            shouldAskForTablou(caseItem, preparing.has(caseItem.id))
        )
        .map((caseItem) => caseItem.id)
    );
  }, [data.cases, actionBuckets, access]);
  const frequencyOverdueByCaseId = useMemo(() => {
    const map: Record<string, string> = {};
    data.cases.forEach((caseItem) => {
      if (caseItem.status !== 'active') return;
      if (access.isShadowed(caseItem.id)) return;
      if (!isFrequencyOverdueActionable(caseItem, nextAppointmentByCaseId)) return;
      if (!caseItem.lastMeetingDate || !caseItem.meetingFrequencyWeeks) return;
      map[caseItem.id] = formatMeetingFrequencyOverdueBy(
        caseItem.lastMeetingDate,
        caseItem.meetingFrequencyWeeks
      );
    });
    return map;
  }, [data.cases, nextAppointmentByCaseId, access]);
  const pulseAppointment = useTimedPulse(5000, !data.loading);

  const handleOpenAppointment = (appointment: Appointment) => {
    navigate(getCalendarDatePath(appointment.date));
  };

  const handleSchedule = (caseItem: Case, session?: number) => {
    setScheduleCaseId(caseItem.id);
    setScheduleSessionNumber(typeof session === 'number' ? session : null);
  };

  const handleCloseSchedule = () => {
    setScheduleCaseId(null);
    setScheduleSessionNumber(null);
  };

  const handleAppointmentScheduled = (appointment: Appointment) => {
    handleCloseSchedule();
    navigate(getCalendarDatePath(appointment.date));
  };

  const handleOpenReportSession = (caseItem: Case, session: number) => {
    navigate(`/session-reports?caseId=${caseItem.id}&session=${session}`);
  };

  const handleOpenReports = (caseItem: { id: string }) => {
    navigate(`/session-reports?caseId=${caseItem.id}`);
  };

  const handleReportSaved = (result?: { sessionNumber: number }) => {
    const caseId = data.selectedCaseForNotes?.id;
    void data.handleSessionReportSaved();
    // Same hand-off as the dashboard: the report is filed, the meeting is still fresh.
    if (caseId) offerAfterReport({ caseId, sessionNumber: result?.sessionNumber ?? null });
  };

  const handleOpenTimeline = (caseItem: Case) => {
    setTimelineCase(caseItem);
  };

  /** Same briefing "Pregătește sesiunea" opens — last session, tablou, tema — without needing
   * a booked appointment; `PrepareSessionDialog` already degrades to "Fără programare setată". */
  const handleOpenDeepen = (caseItem: Case) => {
    setDeepenCase(caseItem);
    setDeepenOpen(true);
  };

  const handleCloseDeepen = () => setDeepenOpen(false);

  /** Cleared only after the close transition, so the dialog does not visibly empty mid-slide. */
  const handleDeepenExited = () => setDeepenCase(null);

  const handleTimelineOpenNotes = () => {
    if (!timelineCase) return;
    const caseForNotes = timelineCase;
    setTimelineCase(null);
    data.handleOpenMeetingNotes(caseForNotes);
  };

  const hasActiveFilters =
    Boolean(data.searchTerm) || data.statusFilter !== 'all' || Boolean(data.caseIdFilter);

  return (
    <div>
      <CasesPageHeader
        caseIdFilter={data.caseIdFilter}
        caseIdsFilter={data.caseIdsFilter}
        focusFilterLabel={data.focusFilterLabel}
        onClearCaseFilter={data.clearCaseIdFilter}
      />

      <CasesToolbar
        searchTerm={data.searchTerm}
        onSearchChange={data.setSearchTerm}
        statusFilter={data.statusFilter}
        onStatusFilterChange={data.handleStatusFilterChange}
        filteredCount={data.filteredCases.length}
        activeCasesCount={data.activeCasesCount}
        waitingCasesCount={data.waitingCasesCount}
      />

      {data.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {data.error}
        </Alert>
      )}

      <CasesGrid
        cases={data.filteredCases}
        loading={data.loading}
        caseNotes={data.caseNotes}
        caseReportsCount={data.caseReportsCount}
        caseTabloulSessions={data.caseTabloulSessions}
        nextAppointmentByCaseId={nextAppointmentByCaseId}
        appointmentBySessionByCaseId={appointmentBySessionByCaseId}
        reportsNeededCaseIds={reportsNeededCaseIds}
        askTabloulCaseIds={askTabloulCaseIds}
        frequencyOverdueByCaseId={frequencyOverdueByCaseId}
        pulseAppointment={pulseAppointment}
        hasActiveFilters={hasActiveFilters}
        onOpenNotes={data.handleOpenMeetingNotes}
        onAddNote={data.handleOpenAddNote}
        onOpenAddReport={data.handleOpenSessionReport}
        onOpenReports={handleOpenReports}
        onOpenReportSession={handleOpenReportSession}
        onSchedule={handleSchedule}
        onOpenTimeline={handleOpenTimeline}
        onDeepen={handleOpenDeepen}
        onEditDescription={setProfileCase}
        onOpenDescription={setProfileCase}
        onOpenAppointment={handleOpenAppointment}
      />


      <MeetingNotes
        open={data.meetingNotesOpen}
        onClose={data.handleCloseMeetingNotes}
        caseId={data.selectedCaseForNotes?.id || ''}
        caseTitle={data.selectedCaseForNotes?.title || ''}
        reportCount={
          data.selectedCaseForNotes
            ? data.caseReportsCount[data.selectedCaseForNotes.id] ?? 0
            : 0
        }
        onNoteAdded={data.handleNoteAdded}
      />

      <MeetingNoteFormDialog
        open={data.addNoteOpen}
        caseItem={data.selectedCaseForNotes}
        reportCount={
          data.selectedCaseForNotes
            ? data.caseReportsCount[data.selectedCaseForNotes.id] ?? 0
            : 0
        }
        onClose={data.handleCloseAddNote}
        onSaved={data.handleNoteAdded}
      />

      <ScheduleAppointmentDialog
        open={scheduleCaseId !== null}
        preSelectedCaseId={scheduleCaseId}
        preSelectedSessionNumber={scheduleSessionNumber}
        onClose={handleCloseSchedule}
        onScheduled={handleAppointmentScheduled}
      />

      <CaseTimelineDialog
        open={timelineCase !== null}
        onClose={() => setTimelineCase(null)}
        caseItem={timelineCase}
        onOpenNotes={handleTimelineOpenNotes}
      />

      <PrepareSessionDialog
        open={deepenOpen}
        caseItem={deepenCase}
        appointment={deepenCase ? nextAppointmentByCaseId[deepenCase.id] : undefined}
        reportCount={deepenCase ? sessionReportCounts[deepenCase.id] ?? 0 : 0}
        onClose={handleCloseDeepen}
        onExited={handleDeepenExited}
        onAddReport={data.handleOpenSessionReport}
      />

      <SessionReport
        open={data.sessionReportOpen}
        onClose={data.handleCloseSessionReport}
        caseId={data.selectedCaseForNotes?.id || ''}
        caseTitle={data.selectedCaseForNotes?.title || ''}
        caseItem={data.selectedCaseForNotes}
        onReportAdded={handleReportSaved}
        onCaseClosed={data.handleCaseClosedFromReport}
        onCancelAddForm={data.handleCloseSessionReport}
        hideAddButton
        caseStatus={data.selectedCaseForNotes?.status}
        autoOpenAddForm
      />


      <CaseProfileDialog
        open={profileCase !== null}
        caseItem={profileCase}
        onClose={() => setProfileCase(null)}
        onSaved={() => void refetch()}
      />

      <Snackbar
        open={data.snackbar.open}
        autoHideDuration={6000}
        onClose={() => data.setSnackbar({ ...data.snackbar, open: false })}
      >
        <Alert
          onClose={() => data.setSnackbar({ ...data.snackbar, open: false })}
          severity={data.snackbar.severity}
          sx={{ width: '100%' }}
        >
          {data.snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Cases;
